import useWebsiteBuilder, {
    StreamingPlatformsStyle,
    WebsiteBuilder_streamingPlatformsExists,
    WebsiteBuilderContent,
    WebsiteBuilderStyle
} from 'Hooks/useWebsiteBuilder';
import { httpsLink, makeKey, styx } from 'modules/Utils';
import { CSSProperties } from 'react';
import { STREAMING_PLATFORMS_INFO } from 'modules/Const';
import Icon from 'elements/Icon';
import ButtonSymphony from 'elements/ButtonSymphony';
import { whiteOrBlackTextColor } from 'helpers/StyleUtils';

interface StreamingPlatformsPreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
}

const StreamingPlatformsPreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
}: StreamingPlatformsPreviewContentProps) => {
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder() as WebsiteBuilder_streamingPlatformsExists;

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;

    const {
        textColor: OLD,
        backgroundColor: backgroundColorWebsiteStyle,
        secondaryColor: secondaryColorWebsiteStyle,
    } = websiteStyle;

    const streamingPlatformsContent = websiteContent[contentIndex].streamingPlatformsContent;
    const streamingPlatformsStyle = streamingPlatformsContent?.style || StreamingPlatformsStyle.STACKED_BOXES;

    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled;
    const contentBackgroundColor = websiteContent[contentIndex].contentBackgroundColor;
    const backgroundColor = isContentBackgroundColorEnabled ? contentBackgroundColor || backgroundColorWebsiteStyle : backgroundColorWebsiteStyle;

    const isSecondaryColorEnabled = websiteContent[contentIndex].isContentSecondaryColorEnabled;
    const secondaryColor = isSecondaryColorEnabled ? websiteContent[contentIndex].contentSecondaryColor || '#FFFFFF' : secondaryColorWebsiteStyle;

    const isContentTextColorEnabled = websiteContent[contentIndex].isContentTextColorEnabled;
    const contentTextColor = websiteContent[contentIndex].contentTextColor

    const textColor = isContentTextColorEnabled ? contentTextColor : whiteOrBlackTextColor(backgroundColor);

    const isContentTextSecondaryColorEnabled = websiteContent[contentIndex].isContentTextSecondaryColorEnabled;
    const contentTextSecondaryColor = websiteContent[contentIndex].contentTextSecondaryColor
    // const secondaryTextColor = isContentTextSecondaryColorEnabled ? contentTextSecondaryColor : whiteOrBlackTextColor(secondaryColor);
    const secondaryTextColor = isSecondaryColorEnabled ? whiteOrBlackTextColor(secondaryColor) : whiteOrBlackTextColor(secondaryColorWebsiteStyle);

    if (!streamingPlatformsContent) {
        return null;
    }

    // NOTE: render()
    return (
        <div
            className="streamingPlatformsPreviewContentComponent"
            style={styx(
                sty.streamingPlatformsPreviewContentComponent,
                streamingPlatformsStyle === StreamingPlatformsStyle.TRADITIONAL ||
                    streamingPlatformsStyle === StreamingPlatformsStyle.STACKED ?
                    {
                        gap: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                    }
                    :
                    null,
                styleProp,
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            {streamingPlatformsContent.platforms.map((platform, index) => {
                const platformInfo = STREAMING_PLATFORMS_INFO[platform.name];
                const platformLink = httpsLink(platform.link);

                if (!platform.isEnabled || !platformLink || !platformInfo) {
                    return null;
                }

                switch (streamingPlatformsStyle) {
                    case StreamingPlatformsStyle.BOXES:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="streamingPlatformsPreviewBoxes"
                                style={styx(
                                    sty.streamingPlatformsPreviewBoxes,
                                    { backgroundColor: secondaryColor }
                                )}
                                onClick={() => {
                                    window.open(platformLink, '_blank');
                                }}
                            >
                                <Icon
                                    className="streamingPlatformsPreviewBoxesIcon"
                                    icon={platformInfo.iconPlatform || platformInfo.icon}
                                    size={100}
                                    color={secondaryTextColor}
                                />
                            </div>
                        )
                    case StreamingPlatformsStyle.STACKED:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="streamingPlatformsPreviewStacked"
                                style={styx(
                                    sty.streamingPlatformsPreviewStacked,
                                    index === 0 ? { borderTop: `1px solid ${textColor}50` } : null,
                                    { borderBottom: `1px solid ${textColor}50` }
                                )}
                            >
                                <Icon
                                    className="streamingPlatformsPreviewStackedIcon"
                                    icon={platformInfo.iconPlatform || platformInfo.icon}
                                    size={140}
                                    color={secondaryTextColor}
                                />
                            </div>
                        )
                    case StreamingPlatformsStyle.STACKED_BOXES:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="streamingPlatformsPreviewStacked"
                                style={styx(
                                    sty.streamingPlatformsPreviewStacked,
                                    { backgroundColor: secondaryColor }
                                )}
                                onClick={() => {
                                    window.open(platformLink, '_blank');
                                }}
                            >
                                <Icon
                                    className="streamingPlatformsPreviewStackedIcon"
                                    icon={platformInfo.iconPlatform || platformInfo.icon}
                                    size={140}
                                    color={secondaryTextColor}
                                />
                            </div>
                        )
                    case StreamingPlatformsStyle.ICONS:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="streamingPlatformsPreviewContentPlatform"
                                style={styx(sty.streamingPlatformsPreviewContentPlatform)}
                            >
                                <Icon
                                    className="streamingPlatformsPreviewContentIcon"
                                    icon={platformInfo.iconFlat || platformInfo.icon}
                                    size={28}
                                    color={textColor}
                                    onClick={() => {
                                        window.open(platformLink, '_blank');
                                    }}
                                />
                            </div>
                        )
                    case StreamingPlatformsStyle.TRADITIONAL:
                        return (
                            <div
                                key={makeKey(platform.name, index, contentIndex)}
                                className="streamingPlatformsPreviewTraditional"
                                style={styx(
                                    sty.streamingPlatformsPreviewTraditional,
                                    index === 0 ? { borderTop: `1px solid ${textColor}50` } : null,
                                    { borderBottom: `1px solid ${textColor}50` }
                                )}
                            >
                                <Icon
                                    className="streamingPlatformsPreviewTraditionalIcon"
                                    icon={platformInfo.iconPlatform || platformInfo.icon}
                                    size={140}
                                    color={textColor}
                                />
                                <ButtonSymphony
                                    className="streamingPlatformsPreviewTraditionalButton"
                                    style={{
                                        backgroundColor: secondaryColor,
                                        color: secondaryTextColor
                                    }}
                                    onClick={() => {
                                        window.open(platformLink, '_blank');
                                    }}
                                >
                                    Play
                                </ButtonSymphony>
                            </div>
                        )
                    default:
                        return null
                }
            })}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    streamingPlatformsPreviewContentComponent: {
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        justifyContent: 'center',
        gap: 8,
        paddingLeft: 16,
        paddingRight: 16,
    },
    streamingPlatformsPreviewContentPlatform: {
        display: 'flex',
        alignItems: 'center',
    },
    streamingPlatformsPreviewBoxes: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'calc(50% - 4px)',
        borderRadius: 8,
        padding: 8,
        cursor: 'pointer',
        gap: 8,
    },
    streamingPlatformsPreviewStacked: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        cursor: 'pointer',
    },
    streamingPlatformsPreviewStackedBoxes: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        borderRadius: 8,
        padding: 8,
        cursor: 'pointer',
    },
    streamingPlatformsPreviewTraditional: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        paddingLeft: 12,
        paddingRight: 16,
        paddingTop: 8,
        paddingBottom: 8,
        cursor: 'pointer',
    }
}

export default StreamingPlatformsPreviewContent;